import React, { useState, useEffect, useContext, useMemo } from "react";
import { UserContext } from "../../../App";
import Table from "./ClassListTable";
import Pagination from "../Pagination";
import ClassListPopUp from "./ClassListPopUp";
import useSortableData from "../../../hooks/useSortableData";
import useSelectableItems from "../../../hooks/useSelectableItems";
import "./RegistrationLink.css";
import "../../../styles/CreateButton.css";
import "../../../styles/common.css";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../config/firebaseConfig";
import { getPerformance, trace } from "firebase/performance";
import MoreOptionsPane from "../../MoreOptionsPane";
import "../../../styles/MoreOptionsButton.css";
import { useTrainingButton } from "../../../hooks/useTrainingElement";

// Helper function to format dates
const formatDate = (dateField) => {
  if (!dateField) return "N/A";
  const date = new Date(dateField);
  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const ClassListsPage = () => {
  const {} = useContext(UserContext);
  const [classLists, setClassLists] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentClassList, setCurrentClassList] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const classListsPerPage = 15;
  const [isLoading, setIsLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Training system
  const createButton = useTrainingButton("class_lists", "class_lists_create");

  const {
    items: sortedClassLists,
    requestSort,
    sortConfig,
  } = useSortableData(classLists);
  const {
    selectedItems: selectedClassLists,
    handleSelectItem,
    handleBulkSelect,
  } = useSelectableItems();

  const fetchClassLists = async () => {
    const fetchClassListsTrace = trace(getPerformance(), "fetchClassLists");
    fetchClassListsTrace.start();
    setIsLoading(true);
    try {
      const getClassListsFunction = httpsCallable(functions, "getClassLists");
      const result = await getClassListsFunction();

      console.log("Fetched class lists:", result.data);

      if (result.data && result.data.classLists) {
        setClassLists(result.data.classLists);
        console.log("Set classLists:", result.data.classLists);
      } else {
        console.error(
          "Invalid response format from getClassLists function:",
          result
        );
        setClassLists([]);
      }
    } catch (error) {
      console.error("Error fetching class lists:", error);
      setClassLists([]);
    } finally {
      setIsLoading(false);
      fetchClassListsTrace.stop();
    }
  };

  useEffect(() => {
    fetchClassLists();
  }, []);

  const handleDeleteClassLists = async () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete the selected class lists?"
    );
    if (confirmDelete) {
      try {
        const deleteClassListsFunction = httpsCallable(
          functions,
          "deleteClassLists"
        );
        await deleteClassListsFunction({
          classListIds: selectedClassLists.map((cl) => cl.id),
        });

        // Update local state
        setClassLists((prevClassLists) =>
          prevClassLists.filter(
            (classList) =>
              !selectedClassLists.some(
                (selected) => selected.id === classList.id
              )
          )
        );
      } catch (error) {
        console.error("Error deleting class lists: ", error);
        alert("Failed to delete selected class lists.");
      }
    }
  };

  const handleDuplicateClassList = async () => {
    if (selectedClassLists.length !== 1) {
      alert("Please select exactly one class list to duplicate.");
      return;
    }

    const classListToDuplicate = selectedClassLists[0];
    const newClassName = prompt(
      "Enter a name for the duplicated class list:",
      `Copy of ${classListToDuplicate.className}`
    );

    if (newClassName) {
      try {
        const duplicateClassListFunction = httpsCallable(
          functions,
          "duplicateClassList"
        );
        const result = await duplicateClassListFunction({
          classListId: classListToDuplicate.id,
          newClassName,
        });

        // Update local state with the new class list
        setClassLists((prevClassLists) => [...prevClassLists, result.data]);
        setShowMoreOptionsPane(false);
      } catch (error) {
        console.error("Error duplicating class list: ", error);
        alert("Failed to duplicate class list.");
      }
    }
  };

  const handleRenameConfirm = async () => {
    if (selectedClassLists.length !== 1) {
      alert("Please select exactly one class list to rename.");
      return;
    }

    const classListToRename = selectedClassLists[0];
    const newClassListName = prompt(
      "Enter new name for the class list:",
      classListToRename.className
    );

    if (newClassListName && newClassListName !== classListToRename.className) {
      try {
        const updateClassListFunction = httpsCallable(
          functions,
          "updateClassList"
        );
        await updateClassListFunction({
          classListId: classListToRename.id,
          updatedData: { className: newClassListName },
        });

        // Update local state
        setClassLists((prevClassLists) =>
          prevClassLists.map((classList) =>
            classList.id === classListToRename.id
              ? { ...classList, className: newClassListName }
              : classList
          )
        );

        setShowMoreOptionsPane(false);
      } catch (error) {
        console.error("Error renaming class list: ", error);
        alert("Failed to rename class list.");
      }
    }
  };

  const openEditModal = (classList) => {
    console.log("Opening edit modal with classList:", classList);
    const teacherIds = Array.isArray(classList.teachers)
      ? classList.teachers
      : [];
    console.log("Teacher IDs:", teacherIds);
    setCurrentClassList({
      ...classList,
      teachers: teacherIds,
    });
    console.log("Set currentClassList:", {
      ...classList,
      teachers: teacherIds,
    });
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    console.log("Opening create modal");
    setCurrentClassList(null);
    setIsEditing(false);
    setIsModalOpen(true);
    // Fetch data after opening the modal
    fetchClassListData();
  };

  const handleUpdateClassList = async (updatedClassList, classListId) => {
    if (!updatedClassList) {
      setIsModalOpen(false);
      return;
    }
    try {
      let result;
      if (classListId) {
        const updateClassListFunction = httpsCallable(
          functions,
          "updateClassList"
        );
        result = await updateClassListFunction({
          classListId: classListId,
          updatedData: updatedClassList,
        });
      } else {
        const createClassListFunction = httpsCallable(
          functions,
          "createClassList"
        );
        result = await createClassListFunction({
          classListData: updatedClassList,
        });
      }

      if (result.data) {
        // Update was successful, refresh the class lists
        await fetchClassLists();
        setIsModalOpen(false);
      } else {
        throw new Error("Failed to create/update class list: No data returned");
      }
    } catch (error) {
      console.error("Error updating/creating class list: ", error);
      alert(`Failed to update/create class list. Error: ${error.message}`);
    }
  };

  const handleRowClick = (e, classList) => {
    setCurrentClassList(classList);
    setIsEditing(true);
    setIsModalOpen(true);
    // Fetch data after opening the modal
    fetchClassListData();
  };

  const filteredClassLists = sortedClassLists.filter((classList) =>
    classList.className.toLowerCase().includes(filterText.toLowerCase())
  );

  const totalPages = Math.ceil(filteredClassLists.length / classListsPerPage);

  const [showMoreOptionsPane, setShowMoreOptionsPane] = useState(false);

  const handleMoreOptionsButtonClick = () => {
    setShowMoreOptionsPane(true);
  };

  const handleDeleteConfirm = () => {
    handleDeleteClassLists();
    setShowMoreOptionsPane(false);
  };

  const fetchClassListData = async () => {
    if (!isDataLoaded) {
      try {
        // First, get teachers and students
        const getClassListDataFunction = httpsCallable(
          functions,
          "getClassListData"
        );
        const classListDataResult = await getClassListDataFunction();
        setTeachers(classListDataResult.data.teachers);
        setStudents(classListDataResult.data.students);

        // Use getCourses function to get only courses accessible to current user
        const getCoursesFunction = httpsCallable(functions, "getCourses");
        const coursesResult = await getCoursesFunction();
        console.log("Filtered courses for user:", coursesResult.data.courses);
        setCourses(coursesResult.data.courses || []);

        setIsDataLoaded(true);
      } catch (error) {
        console.error("Error fetching class list data:", error);
      }
    }
  };

  // Update the formatted lists to handle ISO strings
  const formattedClassLists = useMemo(
    () =>
      filteredClassLists.map((classList) => ({
        ...classList,
        // Keep original ISO strings for sorting
        createdAt: classList.createdAt,
        updatedAt: classList.updatedAt,
      })),
    [filteredClassLists]
  );

  // Add pagination logic to filteredClassLists
  const indexOfLastClassList = currentPage * classListsPerPage;
  const indexOfFirstClassList = indexOfLastClassList - classListsPerPage;
  const currentClassLists = formattedClassLists.slice(
    indexOfFirstClassList,
    indexOfLastClassList
  );

  return (
    <div className="class-lists-page">
      <div className="main-content">
        <div className="top-controls">
          <button
            ref={createButton.ref}
            className={`create-class-list-button ${createButton.className}`}
            onClick={openCreateModal}
            onMouseEnter={createButton.onMouseEnter}
          >
            <span className="button-text-desktop">Create a New Class List</span>
            <span className="button-text-mobile">Create</span>
          </button>
          <div className="filter-container">
            <input
              type="text"
              placeholder="Filter class lists..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="filter-input"
            />
          </div>
        </div>
        <Table
          data={currentClassLists}
          columns={[
            { key: "className", label: "Class List Name" },
            { key: "studentCount", label: "Number of Students" },
            { key: "createdAt", label: "Created" },
            { key: "updatedAt", label: "Last Edited" },
          ]}
          selectedItems={selectedClassLists}
          handleSelectItem={handleSelectItem}
          handleBulkSelect={handleBulkSelect}
          requestSort={requestSort}
          getSortArrow={(key) =>
            sortConfig.key === key ? (
              sortConfig.direction === "ascending" ? (
                <span className="arrow up"></span>
              ) : (
                <span className="arrow down"></span>
              )
            ) : null
          }
          onRowClick={handleRowClick} // Pass the onRowClick handler
          isLoading={isLoading}
          formatters={{
            createdAt: formatDate,
            updatedAt: formatDate,
          }}
        />
        <div className="button-container">
          <button
            className="more-options-button"
            onClick={handleMoreOptionsButtonClick}
          >
            More Options...
          </button>
        </div>
        {showMoreOptionsPane && (
          <MoreOptionsPane
            options={[
              { label: "Rename", action: handleRenameConfirm },
              {
                label: "Duplicate class list",
                action: handleDuplicateClassList,
              },
              { label: "Delete", action: handleDeleteConfirm },
            ]}
            onClose={() => setShowMoreOptionsPane(false)}
          />
        )}
        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          handlePageChange={setCurrentPage}
        />
      </div>
      {isModalOpen && (
        <ClassListPopUp
          classList={currentClassList}
          courses={courses}
          teachers={teachers}
          students={students}
          handleUpdateClassList={handleUpdateClassList}
          type={isEditing ? "edit" : "create"}
          handleClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
};

export default ClassListsPage;
